import { useCallback, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useLocation } from 'react-router-dom'
import { Button, type ITabItem, Tabs, Typography, Tooltip, Icon } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { checkAccessPage, EnumAccessPageKeys } from 'entities/ExportRouters/lib/checkAccessPage'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { GracePeriodAlert } from 'entities/Phone'
import { AssignedNumbers } from 'pages/settings/pages/numbers/ui/AssignedNumbers/AssignedNumbers'
import { UnassignedNumbers } from 'pages/settings/pages/numbers/ui/UnassignedNumbers/UnassignedNumbers'
import { NumbersStore } from 'pages/settings/pages/numbers/store/numbersStore'
import { NumbersPath } from 'pages/settings/pages/numbers/route/numbersPath'
import type { IManageNumbersConfig } from 'pages/settings/pages/numbers/ui/ManageNumbers/store/type'
import styles from './styles.module.scss'

const tabs: ITabItem[] = [
  {
    name: 'Assigned',
    key: 'assigned',
  },
  {
    name: 'Unassigned',
    key: 'unassigned',
  },
]

export const NumbersPage = observer(() => {
  const entities = useEntitiesContext()
  const { organizationStore } = entities
  const { isNumbersGracePeriod } = organizationStore
  const location = useLocation()
  const [activeTab, setActiveTab] = useState(tabs[0].key)
  const numbersStore = useMemo(() => new NumbersStore(entities), [entities])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const tabParam = searchParams.get('tab')

    if (tabParam === 'unassigned') {
      setActiveTab('unassigned')
    }
  }, [location.search])

  useEffect(() => {
    return () => {
      numbersStore.dispose()
    }
  }, [numbersStore])

  const {
    usedLicense,
    usedLicenseDiff,
    onManageNumbers,
    manageNumberWrapper,
    importNumbersStatus,
  } = numbersStore

  const handleManageNumbers = () => {
    const config: IManageNumbersConfig = isNumbersGracePeriod
      ? {
          type: 'manageByGracePeriod',
        }
      : { type: 'default' }
    manageNumberWrapper(() => onManageNumbers(config), { skipCanceledCheck: true })
  }

  const handlePayNow = () => {
    manageNumberWrapper(
      () =>
        onManageNumbers({
          type: 'payNowByGracePeriod',
        }),
      { skipCanceledCheck: true }
    )
  }

  const handleAddNumber = () => {
    manageNumberWrapper(() => uiStore.changeRoute({ path: `/${NumbersPath.addNumber.root}` }))
  }

  const setAssignedActiveTab = useCallback(() => setActiveTab('assigned'), [])

  return (
    <div className={styles.content}>
      <header>
        <div className={styles.integrationDetailsHeader}>
          <Typography variant='heading-lg' ariaLabel='Numbers_title'>
            Numbers
          </Typography>
          <div className={styles.title}>
            <Typography
              variant='body-md-regular'
              ariaLabel='Numbers_subtitle_text'
              color='tertiary'
            >
              Number licenses used:
            </Typography>
            <Tooltip
              placement='top'
              label={
                isNumbersGracePeriod ? (
                  <>
                    You’re using {usedLicenseDiff} more numbers than your
                    <br /> licenses cover. You have {importNumbersStatus.durationHours} hours to
                    make
                    <br /> changes.
                  </>
                ) : null
              }
            >
              <Typography
                className={styles.count}
                variant='body-md-regular'
                ariaLabel='Numbers_subtitle_value'
              >
                {usedLicense}
                {isNumbersGracePeriod && (
                  <Icon
                    className={styles.icon}
                    icon='alertTriangleYellow'
                    color={'var(--yellow-60)'}
                  />
                )}
              </Typography>
            </Tooltip>
          </div>
        </div>
        {checkAccessPage(EnumAccessPageKeys.numbers_settings_add, entities) && (
          <div className={styles.actions}>
            <Button
              text='Manage licenses'
              contained='secondary'
              iconProps={{ icon: 'checkVerified', color: 'tertiary' }}
              size='medium'
              onClick={handleManageNumbers}
            />
            <Button
              text='Add number'
              iconProps={{ icon: 'add' }}
              size='medium'
              onClick={handleAddNumber}
              disabled={isNumbersGracePeriod}
              tooltipProps={
                isNumbersGracePeriod
                  ? {
                      label: (
                        <Typography
                          variant={'body-md-regular'}
                          ellipsis
                          color={'var(--content-primary-primary-inverted)'}
                          ariaLabel={'Grace Period'}
                        >
                          Take care of unpaid licenses
                          <br /> before adding new numbers
                        </Typography>
                      ),
                      width: 233,
                      placement: 'top',
                    }
                  : undefined
              }
            />
          </div>
        )}
      </header>
      {isNumbersGracePeriod && (
        <GracePeriodAlert
          className={styles.alerts}
          importNumbersStatus={importNumbersStatus}
          actions={[
            {
              variant: 'button',
              type: 'primary',
              text: 'Pay now',
              onClick: () => handlePayNow(),
            },
          ]}
        />
      )}
      <div className={styles.navBar}>
        {checkAccessPage(EnumAccessPageKeys.numbers_settings_add, entities) && (
          <Tabs
            tabs={tabs}
            activeTabKey={activeTab}
            handleSelectTab={(tab) => setActiveTab(tab.key)}
            tabsClassName={styles.navBarTabs}
          />
        )}
        {activeTab === 'assigned' && <AssignedNumbers />}
        {activeTab === 'unassigned' && (
          <UnassignedNumbers
            onSuccessAssignNumber={setAssignedActiveTab}
            manageNumberWrapper={manageNumberWrapper}
          />
        )}
      </div>
    </div>
  )
})
